import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {object} SliderAutoplayRefs
 * @property {HTMLElement} [slider] - The inner `<slider-component>` element.
 * @property {HTMLButtonElement[]} [bars] - Optional navigation bar buttons (e.g. banner-slider).
 * @property {HTMLElement[]} [barLabels] - Optional per-bar label elements, filled from each slide's data attribute.
 * @property {HTMLButtonElement} [playToggle] - Optional play/pause toggle button (RGAA 13.8).
 */

/**
 * Minimal public surface of the inner `<slider-component>` that we drive.
 * @typedef {HTMLElement & {
 *   refs?: { slides?: HTMLElement | HTMLElement[] },
 *   handleDotClick?: (index: number) => void,
 *   handleNextClick?: () => void,
 *   handlePrevClick?: () => void,
 * }} SliderControl
 */

/**
 * Slider autoplay — a section-agnostic orchestration wrapper around `<slider-component>`.
 *
 * It owns autoplay (pause on hover, pause on focus-within, an explicit play/pause
 * toggle for RGAA 13.8, reduced-motion + design-mode guards), plays the active
 * slide's background video, and — when navigation bars are present — drives their
 * progress fill. The inner slider-component keeps ownership of Embla, accessibility,
 * keyboard handling and the theme editor sync; this wrapper only talks to it through
 * its public API and the bubbling `slider:init` / `slider:select` events.
 *
 * The navigation chrome is left to each consumer: banner-slider renders progress
 * bars (wired to `/goTo/{index}`), hero renders prev/next arrows (`/prev`, `/next`)
 * plus a pause toggle (`/togglePlay`). Bar/label logic no-ops when those refs are
 * absent, so the same component drives both with no per-section JS.
 *
 * Autoplay runs on a single `requestAnimationFrame` clock that tracks elapsed time.
 * The loop stays alive while connected; it simply stops accumulating while paused,
 * hovered (when enabled), focused within, or while the tab is hidden, then resumes
 * seamlessly — so it never gets stuck after a tab switch or losing focus.
 *
 * @extends {Component<SliderAutoplayRefs>}
 */
class SliderAutoplay extends Component {
  #index = 0;
  #count = 0;
  #reduced = false;
  #hovered = false;
  #focusWithin = false;
  /** Persistent pause state driven by the play/pause toggle. */
  #paused = false;

  /** @type {SliderControl | null} */
  #slider = null;
  /** @type {number | null} */
  #rafId = null;
  /** @type {number | null} */
  #lastTick = null;
  /** Milliseconds accumulated within the current slide's cycle. */
  #elapsed = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#reduced = prefersReducedMotion();
    this.#resolveSlider();
    this.#syncCount();

    // Events bubble up from the inner slider-component.
    this.addEventListener(ThemeEvents.sliderInit, this.#onInit);
    this.addEventListener(ThemeEvents.sliderSelect, this.#onSelect);

    // Pause autoplay on hover and on focus-within. Tab visibility is handled inside
    // the tick so the loop can never get stuck paused.
    this.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);

    // Sync once after the inner slider-component has connected and Embla has
    // initialized (next frame), which sidesteps the upgrade-order race where
    // `slider:init` may fire before this wrapper is defined.
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#syncCount();
      this.#syncLabels();
      this.#syncPlayToggle();
      this.#applySelection(this.#index);
      this.#startLoop();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopLoop();
    this.#pauseAllVideos();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#resolveSlider();
    this.#syncCount();
    if (this.#count > 0 && this.#index >= this.#count) this.#index = 0;
    this.#syncLabels();
    this.#syncPlayToggle();
    this.#applySelection(this.#index);
    if (this.#canAutoplay) this.#startLoop();
    else this.#stopLoop();
  }

  /**
   * Navigate to a slide. Declarative target of the bar buttons
   * (`on:click="/goTo/{index}"`). The resulting `slider:select` resets the
   * progress cycle, keeping the fill and the auto-advance in sync.
   * @param {number | string} index
   */
  goTo(index) {
    const target = Number(index);
    if (!Number.isFinite(target)) return;
    this.#slider?.handleDotClick?.(target);
    this.#startLoop();
  }

  /** Advance to the next slide (declarative target of the next arrow). */
  next() {
    this.#slider?.handleNextClick?.();
    this.#startLoop();
  }

  /** Go to the previous slide (declarative target of the prev arrow). */
  prev() {
    this.#slider?.handlePrevClick?.();
    this.#startLoop();
  }

  /**
   * Toggle the persistent play/pause state (declarative target of the pause
   * button — RGAA 13.8 / WCAG 2.2.2 Pause, Stop, Hide).
   */
  togglePlay() {
    this.#paused = !this.#paused;
    if (!this.#paused) this.#resetCycle();
    this.#syncPlayToggle();
  }

  /** Derive the slide count from the inner slider-component, falling back to bars. */
  #syncCount() {
    const slides = this.#slides().length;
    this.#count = slides > 0 ? slides : this.#bars.length;
  }

  /**
   * Copy each slide's `data-index-label` into the matching navigation bar.
   * Theme block settings aren't reliably readable from the parent section's
   * loop, so the label is emitted by the slide block itself and mirrored here.
   * No-ops when there are no bars/labels (e.g. arrow-only hero).
   */
  #syncLabels() {
    const slides = this.#slides();
    const labels = this.refs.barLabels;
    const labelEls = Array.isArray(labels) ? labels : [];

    labelEls.forEach((el, i) => {
      const value = slides[i]?.dataset.indexLabel;
      if (value == null) return;
      el.textContent = value;
      const bar = this.#bars[i];
      if (bar && value) {
        const base = bar.getAttribute('aria-label') ?? '';
        if (!base.includes(value)) bar.setAttribute('aria-label', `${base} — ${value}`.trim());
      }
    });
  }

  /**
   * Reflect the play/pause toggle: keep its label/state in sync, and hide it
   * entirely when autoplay can't run (single slide, reduced motion, no Embla)
   * since there's no moving content to pause.
   */
  #syncPlayToggle() {
    const toggle = this.refs.playToggle;
    if (!(toggle instanceof HTMLButtonElement)) return;

    const possible = this.#autoplayPossible;
    toggle.hidden = !possible;
    if (!possible) return;

    const playing = !this.#paused;
    toggle.dataset.state = playing ? 'playing' : 'paused';
    toggle.setAttribute('aria-pressed', String(this.#paused));
    const label = playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay;
    if (label) toggle.setAttribute('aria-label', label);
  }

  /** @returns {HTMLButtonElement[]} */
  get #bars() {
    const ref = this.refs.bars;
    return Array.isArray(ref) ? ref : [];
  }

  /** @returns {number} */
  get #intervalMs() {
    const value = Number(this.dataset.interval);
    return Number.isFinite(value) && value >= 1000 ? value : 6000;
  }

  /**
   * Whether autoplay is configured and possible at all (ignores the transient
   * paused/hovered/focused state). Drives play-toggle visibility.
   * @returns {boolean}
   */
  get #autoplayPossible() {
    return (
      this.dataset.autoplay !== 'false' &&
      !this.#reduced &&
      this.#count > 1 &&
      !window.Shopify?.designMode &&
      typeof window.EmblaCarousel !== 'undefined'
    );
  }

  /** @returns {boolean} Whether the autoplay loop should run. */
  get #canAutoplay() {
    return this.#autoplayPossible;
  }

  #resolveSlider() {
    const ref = this.refs.slider;
    this.#slider = ref instanceof HTMLElement ? /** @type {SliderControl} */ (ref) : null;
  }

  /** @returns {HTMLElement[]} */
  #slides() {
    const ref = this.#slider?.refs?.slides;
    if (Array.isArray(ref)) return ref;
    return ref instanceof HTMLElement ? [ref] : [];
  }

  /** @param {Event} event */
  #onInit = (event) => {
    if (event.target !== this.#slider) return;
    this.#syncCount();
    this.#syncLabels();
    this.#syncPlayToggle();
    this.#applySelection(this.#index);
    this.#startLoop();
  };

  /** @param {Event} event */
  #onSelect = (event) => {
    if (event.target !== this.#slider) return;
    const detail = /** @type {CustomEvent<{ index: number }>} */ (event).detail;
    const index = Number(detail?.index);
    this.#applySelection(Number.isFinite(index) ? index : this.#index);
  };

  #onPointerEnter = () => {
    this.#hovered = true;
  };

  #onPointerLeave = () => {
    this.#hovered = false;
  };

  #onFocusIn = () => {
    this.#focusWithin = true;
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    // relatedTarget is the element receiving focus; if it's still inside, stay paused.
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;
    this.#focusWithin = false;
  };

  /**
   * Reflect the active slide: highlight its bar, play its background video, and
   * reset the progress cycle so the fill restarts from zero on this slide.
   * @param {number} index
   */
  #applySelection(index) {
    this.#index = index;

    this.#bars.forEach((bar, i) => {
      const active = i === index;
      bar.classList.toggle('banner-slider__bar--active', active);
      bar.setAttribute('aria-current', String(active));
    });

    this.#playActiveVideo(index);
    this.#resetCycle();
    this.#renderFill(0);
  }

  /** @param {number} index */
  #playActiveVideo(index) {
    this.#slides().forEach((slide, i) => {
      const video = slide.querySelector('video');
      if (!(video instanceof HTMLVideoElement)) return;

      if (i === index && !this.#reduced) {
        video.muted = true;
        try {
          video.currentTime = 0;
        } catch {
          // currentTime can throw before metadata is ready; ignore.
        }
        video.play()?.catch(() => {});
      } else {
        video.pause();
        try {
          video.currentTime = 0;
        } catch {
          // ignore
        }
      }
    });
  }

  /**
   * Render the progress fill on navigation bars (no-op when there are no bars).
   * While autoplay drives it, the active bar is set to `scaleX(progress)` and the
   * others reset to 0. When autoplay isn't running, inline styles are cleared so
   * the CSS default applies (active bar shown full).
   * @param {number} progress 0..1
   */
  #renderFill(progress) {
    const driving = this.#canAutoplay && !this.#paused;
    this.#bars.forEach((bar, i) => {
      const fill = bar.querySelector('.banner-slider__bar-fill');
      if (!(fill instanceof HTMLElement)) return;
      if (!driving) {
        fill.style.transform = '';
      } else {
        fill.style.transform = `scaleX(${i === this.#index ? progress : 0})`;
      }
    });
  }

  #resetCycle() {
    this.#elapsed = 0;
    this.#lastTick = null;
  }

  #startLoop() {
    if (this.#rafId != null) return;
    if (!this.#canAutoplay) return;
    this.#lastTick = null;
    this.#rafId = requestAnimationFrame(this.#tick);
  }

  #stopLoop() {
    if (this.#rafId != null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
    this.#lastTick = null;
  }

  /** @param {number} now */
  #tick = (now) => {
    if (this.#rafId == null) return;

    const delta = this.#lastTick == null ? 0 : now - this.#lastTick;
    this.#lastTick = now;

    // Only advance time while visible and not paused/hovered/focused. Ignore large
    // gaps from background-tab throttling so returning to the tab never jumps.
    const pauseOnHover = this.dataset.pauseOnHover === 'true';
    const running =
      !this.#paused && !(pauseOnHover && this.#hovered) && !this.#focusWithin && !document.hidden;
    if (running && delta > 0 && delta < 1000) this.#elapsed += delta;

    const interval = this.#intervalMs;
    const progress = interval > 0 ? Math.min(this.#elapsed / interval, 1) : 0;
    this.#renderFill(progress);

    if (running && this.#elapsed >= interval) {
      this.#elapsed = 0;
      // Advancing fires `slider:select` → #applySelection, which resets the cycle.
      this.#slider?.handleNextClick?.();
    }

    this.#rafId = requestAnimationFrame(this.#tick);
  };

  #pauseAllVideos() {
    this.#slides().forEach((slide) => {
      const video = slide.querySelector('video');
      if (video instanceof HTMLVideoElement) video.pause();
    });
  }
}

if (!customElements.get('slider-autoplay')) {
  customElements.define('slider-autoplay', SliderAutoplay);
}
